import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { registerUser } from '../services/authService';
import { checkRegistrationAvailable } from '../utils/userLimits';
import { addToWaitlist } from '../services/waitlist';
import TivraLogo from '../assets/tivra_logo.svg';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(true);
  const { signup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const checkRegistration = async () => {
      const isAvailable = await checkRegistrationAvailable();
      setRegistrationOpen(isAvailable);
    };
    checkRegistration();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (registrationOpen) {
      if (password !== confirmPassword) {
        setLoading(false);
        return setError('Passwords do not match');
      }

      try {
        await registerUser(email, password, '');
        navigate('/app');
      } catch (error) {
        setError('Failed to create an account: ' + error.message);
      }
    } else {
      try {
        await addToWaitlist(email);
        setSuccess('Thank you! We\'ll notify you when registration reopens.');
        setEmail('');
      } catch (error) {
        setError('Failed to join waitlist: ' + error.message);
      }
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F8F9FA',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <RouterLink to="/">
          <img src={TivraLogo} alt="Tivra Logo" style={{ marginBottom: '20px', width: '200px' }} />
        </RouterLink>
      </Box>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          borderRadius: 2
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 3,
            fontWeight: 600,
            textAlign: 'center'
          }}
        >
          {registrationOpen ? 'Sign Up' : 'Join Waitlist'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {!registrationOpen && (
          <Typography sx={{ mb: 3, textAlign: 'center' }}>
            We've received huge interest in early access. Please leave your email address to be notified when new registrations open.
          </Typography>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            sx={{ mb: 2 }}
          />

          {registrationOpen && (
            <>
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
              />

              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                fullWidth
                sx={{ mb: 2 }}
              />
            </>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{
              textTransform: 'none',
              py: 1.5,
              backgroundColor: '#2D68FE',
            }}
          >
            {loading ? 'Processing...' : (registrationOpen ? 'Sign Up' : 'Join Waitlist')}
          </Button>
        </form>

        <Box sx={{ 
          mt: 2,
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Link 
            href="/login" 
            variant="body2"
            sx={{ 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Already have an account? Sign In
          </Link>
          <Link 
            href="/forgot-password" 
            variant="body2"
            sx={{ 
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            Forgot password?
          </Link>
        </Box>
      </Paper>
      <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
        <Link
          href="/privacy"
          variant="body2"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >Privacy Policy</Link> | <Link
          href="/terms"
          variant="body2"
          sx={{
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >Terms of use</Link>
      </Typography>
    </Box>
  );
}
